"use client";
import React from 'react';
import { motion } from 'framer-motion';

const CLASH = 'var(--font-clash), "Clash Display", sans-serif';
const MANROPE = 'var(--font-manrope), Manrope, sans-serif';
const LIME = '#ceff1a';

const rows = [
    { seo: 'Search queries', geo: 'Conversational questions' },
    { seo: 'Keywords', geo: 'Topics, entities & concepts' },
    { seo: 'Ranking pages', geo: 'Being retrieved as a useful source' },
    { seo: 'Search results', geo: 'AI-generated responses + supporting links' },
    { seo: 'Clicks & rankings', geo: 'Visibility, qualified discovery & conversions' },
    { seo: 'Content relevance', geo: 'Content relevance + clarity + authority' },
];

function ArrowIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" className="shrink-0 rotate-90 md:rotate-0">
            <path d="M2 8h11M9 4l4 4-4 4" stroke={LIME} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function Row({ index, seo, geo }: { index: number; seo: string; geo: string }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.35, delay: 0.1 + index * 0.05 }}
            className="flex flex-col gap-2 md:grid md:grid-cols-[1fr,auto,1fr] md:items-center md:gap-4 px-5 md:px-7 py-3.5"
            style={{ borderTop: index === 0 ? 'none' : '1px solid rgba(255,255,255,0.07)' }}
        >
            <span className="text-sm md:text-[15px] md:text-right" style={{ fontFamily: MANROPE, color: 'rgba(255,255,255,0.4)', textDecoration: 'line-through', textDecorationColor: 'rgba(255,255,255,0.18)' }}>
                {seo}
            </span>
            <ArrowIcon />
            <span className="text-sm md:text-[15px] font-semibold" style={{ fontFamily: MANROPE, color: '#fbfdfd' }}>
                {geo}
            </span>
        </motion.div>
    );
}

export default function AISEOComparison() {
    return (
        <section className="relative overflow-hidden py-14 md:py-20 px-6 md:px-16 lg:px-24" style={{ background: '#0f0f0f' }}>
            <div className="max-w-[820px] mx-auto flex flex-col gap-8">
                <div className="flex flex-col gap-3">
                    <motion.span
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="text-xs font-bold tracking-[0.2em] uppercase"
                        style={{ color: LIME, fontFamily: CLASH }}
                    >
                        The Shift
                    </motion.span>
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className="text-3xl md:text-4xl font-bold tracking-tight leading-tight"
                        style={{ fontFamily: CLASH, color: '#fbfdfd' }}
                    >
                        SEO vs AI SEO
                    </motion.h2>
                </div>

                {/* comparison card */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                    className="rounded-2xl overflow-hidden"
                    style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.1)' }}
                >
                    {/* header */}
                    <div
                        className="flex items-center justify-between px-5 md:px-7 py-3"
                        style={{ background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}
                    >
                        <span className="text-[11px] font-bold uppercase tracking-wider" style={{ fontFamily: CLASH, color: 'rgba(255,255,255,0.45)' }}>
                            Traditional SEO
                        </span>
                        <span className="text-[11px] font-bold uppercase tracking-wider" style={{ fontFamily: CLASH, color: LIME }}>
                            AI Search / GEO
                        </span>
                    </div>

                    {rows.map((row, i) => (
                        <Row key={i} index={i} seo={row.seo} geo={row.geo} />
                    ))}
                </motion.div>

                <motion.p
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.15 }}
                    className="text-sm md:text-base leading-relaxed text-center"
                    style={{ fontFamily: MANROPE, color: 'rgba(255,255,255,0.6)', letterSpacing: '0.01em' }}
                >
                    The important distinction: <span style={{ color: LIME, fontWeight: 600 }}>AI SEO doesn&apos;t replace SEO. It builds on it.</span>
                </motion.p>
            </div>
        </section>
    );
}
